import { Injectable } from '@angular/core';
import { PendoService } from '../../../shared/services/analytics/pendo.service';
import { IDataRule, SidebarFolderId } from '../../data-rules-model';

type RuleLinkInteraction = 'click' | 'ctrl-click' | 'shift-click' | 'middle-click' | 'right-click';

@Injectable({ providedIn: 'root' })
export class DataRulesFoldersPendoService {
  constructor(private pendoService: PendoService) {}

  trackFolderCreated(name: string, totalUserFolders: number, source: string): void {
    this.recordEvent('DataRules Folders | Folder Created', {
      folderName: this.pendoService.truncateForPendo(name, 100),
      nameLength: name.length,
      nameWordCount: name.split(/\s+/).filter(Boolean).length,
      totalUserFolders,
      source
    });
  }

  trackFolderRenamed(name: string): void {
    this.recordEvent('DataRules Folders | Folder Renamed', {
      newFolderName: this.pendoService.truncateForPendo(name, 100),
      newNameLength: name.length,
      newNameWordCount: name.trim().split(/\s+/).filter(Boolean).length
    });
  }

  trackFolderDeleted(name: string, rulesDisconnected: number, remainingUserFolders: number): void {
    this.recordEvent('DataRules Folders | Folder Deleted', {
      folderName: this.pendoService.truncateForPendo(name, 100),
      rulesDisconnected,
      remainingUserFolders
    });
  }

  trackFolderSelected(folderId: string, name: string, ruleCount: number, totalUserFolders: number): void {
    const customName = this.customFolderName(folderId, name);
    this.recordEvent('DataRules Folders | Folder Selected', {
      folderKind: this.folderKindFor(folderId),
      ...(customName ? { folderName: customName } : {}),
      ruleCount,
      totalUserFolders
    });
  }

  trackCreateFolderPopupOpened(existingUserFolders: number): void {
    this.recordEvent('DataRules Folders | Folder Popup Opened', {
      source: 'sidebar-add-folder',
      mode: 'create-folder',
      existingUserFolders
    });
  }

  trackRuleFolderPopupOpened(rule: IDataRule): void {
    this.recordEvent('DataRules Folders | Folder Popup Opened', {
      source: 'table-rule-row',
      mode: 'manage-folders',
      ruleFolderCount: rule.ruleData.folders?.filter(f => f.id !== SidebarFolderId.ARCHIVE).length ?? 0,
      isArchived: rule.ruleData.folders?.some(f => f.id === SidebarFolderId.ARCHIVE) ?? false,
      ruleEntity: this.safeEntity(rule.ruleData.source?.config?.entity)
    });
  }

  trackRuleAddedToFolder(folderId: string, folderName: string | undefined, ruleEntity: string | undefined, source: string): void {
    const customName = this.customFolderName(folderId, folderName);
    this.recordEvent('DataRules Folders | Rule Added to Custom Folder', {
      ...(customName ? { folderName: customName } : {}),
      ruleEntity: this.safeEntity(ruleEntity),
      source
    });
  }

  trackRuleRemovedFromFolder(folderId: string, folderName: string | undefined, ruleEntity: string | undefined, source: string): void {
    const customName = this.customFolderName(folderId, folderName);
    this.recordEvent('DataRules Folders | Rule Removed from Custom Folder', {
      ...(customName ? { folderName: customName } : {}),
      ruleEntity: this.safeEntity(ruleEntity),
      source
    });
  }

  trackRuleArchived(context: { userFolderCountBefore: number; wasScheduled: boolean; ruleEntity: string | undefined }, source: string, confirmed: boolean): void {
    this.recordEvent('DataRules Folders | Rule Archived', {
      source,
      confirmed,
      userFolderCountBefore: context.userFolderCountBefore,
      wasScheduled: context.wasScheduled,
      ruleEntity: this.safeEntity(context.ruleEntity)
    });
  }

  trackRuleUnarchived(ruleEntity: string | undefined, source = 'rule-popup-manage-folders'): void {
    this.recordEvent('DataRules Folders | Rule Unarchived', {
      ruleEntity: this.safeEntity(ruleEntity),
      source
    });
  }

  trackRuleNameLinkUsed(event: MouseEvent, rule: IDataRule): void {
    const interaction = this.ruleLinkInteractionFor(event);
    if (!interaction) {
      return;
    }
    this.recordEvent('DataRules Folders | Rule Name Link Used', {
      interaction,
      ruleEntity: this.safeEntity(rule.ruleData.source?.config?.entity)
    });
  }

  trackRowLoadingStarted(
    folderId: string, folderName: string | undefined, entityFilter: string, totalRuleCount: number,
    initialRowsShown: number, triggerSource: 'scrolled-near-bottom' | 'jumped-to-bottom'
  ): void {
    const customName = this.customFolderName(folderId, folderName);
    this.recordEvent('DataRules Folders | Row Loading Started', {
      folderKind: this.folderKindFor(folderId),
      ...(customName ? { folderName: customName } : {}),
      entityFilter: this.safeEntity(entityFilter),
      totalRuleCount,
      initialRowsShown,
      triggerSource
    });
  }

  trackAllRowsLoaded(
    folderId: string, folderName: string | undefined, entityFilter: string, totalRuleCount: number, loadTimeMs: number, chunkCount: number
  ): void {
    const customName = this.customFolderName(folderId, folderName);
    this.recordEvent('DataRules Folders | All Rows Loaded', {
      folderKind: this.folderKindFor(folderId),
      ...(customName ? { folderName: customName } : {}),
      entityFilter: this.safeEntity(entityFilter),
      totalRuleCount,
      loadTimeMs,
      chunkCount
    });
  }

  private ruleLinkInteractionFor(event: MouseEvent): RuleLinkInteraction | null {
    if (event.type === 'contextmenu') {
      return 'right-click';
    }
    if (event.type === 'auxclick') {
      // auxclick also fires for the right button, which is already counted via contextmenu
      return event.button === 1 ? 'middle-click' : null;
    }
    if (event.ctrlKey || event.metaKey) {
      return 'ctrl-click';
    }
    if (event.shiftKey) {
      return 'shift-click';
    }
    return 'click';
  }

  private folderKindFor(folderId: string): string {
    switch (folderId) {
      case '':
      case undefined:
      case null:
        return 'unknown';
      case 'all':
        return 'all';
      case 'scheduled':
        return 'system-scheduled';
      case 'unsorted':
        return 'system-unsorted';
      case 'archive':
        return 'system-archive';
      case 'kyloe-ai':
        return 'product-ai-assist';
      case 'kyloe-awesomeDocs':
        return 'product-awesome-docs';
      default:
        return 'custom-folder';
    }
  }

  private customFolderName(folderId: string, name: string | undefined): string | undefined {
    if (!name || this.folderKindFor(folderId) !== 'custom-folder') {
      return undefined;
    }
    return this.pendoService.truncateForPendo(name, 100);
  }

  private safeEntity(entity: string | undefined): string {
    return this.pendoService?.truncateForPendo(entity ?? 'unknown') ?? String(entity ?? 'unknown');
  }

  private recordEvent(name: string, data: Record<string, unknown>): void {
    // console.log(`%c ${name}`, 'background: #00B398; color: #BADA55; font-size:22px', data);
    this.pendoService?.recordEvent(name, data);
  }
}
